import { config } from "../config";
import { tokenManager } from "./tokenManager";
import { isNative } from "@/utils/platform";
import { nativeTokenStore } from "./nativeTokenStore";

let lastValidationTime: number | null = null;
const VALIDATION_COOLDOWN = 15 * 60 * 1000;

let nativeUserIdCache: number | null = null;
let nativeHasTokensCache: boolean = false;
let nativeBootstrapDone = false;

export const bootstrapNativeAuthCache = async (): Promise<void> => {
  if (!isNative()) {
    nativeBootstrapDone = true;
    return;
  }
  try {
    // Adopt any tokens the share extension refreshed while the app was closed,
    // before the auth cache reads a possibly-stale session.
    await nativeTokenStore.syncFromShared();
    nativeUserIdCache = await nativeTokenStore.getUserId();
    nativeHasTokensCache = await nativeTokenStore.hasTokens();
    if (nativeHasTokensCache) {
      await nativeTokenStore.syncToShared();
    }
  } catch {
    nativeUserIdCache = null;
    nativeHasTokensCache = false;
  } finally {
    nativeBootstrapDone = true;
  }
};

const getUserIdFromCookie = (): number | null => {
  const cookies = document.cookie.split(';');
  const userIdCookie = cookies.find(cookie => cookie.trim().startsWith('user_id='));
  if (userIdCookie) {
    const value = userIdCookie.split('=')[1];
    const userId = parseInt(value, 10);
    return isNaN(userId) ? null : userId;
  }
  return null;
};

const setUserIdCookie = (userId: number) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + 30);
  document.cookie = `user_id=${userId}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
};

const clearUserIdCookie = () => {
  document.cookie = 'user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure';
};

export const setUserId = (userId: number) => {
  if (isNative()) {
    nativeUserIdCache = userId;
    nativeHasTokensCache = true;
    return;
  }
  setUserIdCookie(userId);
};

export const clearUserId = () => {
  if (isNative()) {
    nativeUserIdCache = null;
    nativeHasTokensCache = false;
  }
  clearUserIdCookie();
  lastValidationTime = null;
};

export const hasStoredUserId = () => {
  if (isNative()) {
    return nativeUserIdCache !== null;
  }
  return getUserIdFromCookie() !== null;
};

export const hasAuthTokens = () => {
  if (isNative()) {
    return nativeHasTokensCache;
  }
  const cookies = document.cookie.split(';');
  return cookies.some(cookie => {
    const trimmed = cookie.trim();
    return trimmed.startsWith('access_token=') || trimmed.startsWith('refresh_token=');
  });
};

export const shouldAttemptValidation = (): boolean => {
  return !hasStoredUserId();
};

const getStoredUserIdSync = (): number | null => {
  if (isNative()) {
    return nativeUserIdCache;
  }
  return getUserIdFromCookie();
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const validate = async (force: boolean = false, userId?: number, retryCount: number = 0) => {
  if (isNative() && !nativeBootstrapDone) {
    await bootstrapNativeAuthCache();
  }

  const now = Date.now();
  const userIdToUse = userId || getStoredUserIdSync();

  if (!force && lastValidationTime && (now - lastValidationTime < VALIDATION_COOLDOWN)) {
    return null;
  }

  const MAX_RETRIES = 2;
  const TIMEOUT = 10000;

  try {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), TIMEOUT);

    const endpoint = userIdToUse
      ? `${config.API_URL}/user/${userIdToUse}/validate`
      : `${config.API_URL}/user/validate`;

    const response = await tokenManager.makeAuthenticatedRequest(endpoint, {
      method: 'GET',
      signal: abortController.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        if (!userIdToUse) {
          throw new Error('SSO_AUTHENTICATION_FAILED');
        }
        throw new Error('SESSION_EXPIRED');
      }

      if (response.status === 411) {
        if (retryCount < MAX_RETRIES) {
          const backoffDelay = Math.min(500 * (retryCount + 1), 2000);
          await sleep(backoffDelay);
          return validate(force, userId, retryCount + 1);
        }
        throw new Error('SESSION_EXPIRED');
      }

      let errorText;
      try {
        const errorData = await response.json();
        errorText = errorData.detail || errorData.message || errorData.error;
      } catch {
        errorText = await response.text();
      }

      if (!userIdToUse && (errorText.includes('not found') || errorText.includes('User ID required'))) {
        throw new Error('NO_VALID_SESSION');
      }

      throw new Error(errorText || 'Session validation failed');
    }

    const data = await response.json();
    lastValidationTime = now;

    if (data && data.id && !getStoredUserIdSync()) {
      setUserId(data.id);
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError' || error.message.includes('fetch')) {
        if (retryCount < MAX_RETRIES) {
          const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          await sleep(backoffDelay);
          return validate(force, userId, retryCount + 1);
        }
        throw new Error('NETWORK_TIMEOUT');
      }

      if (error.message.includes('SESSION_EXPIRED') ||
          error.message.includes('SSO_AUTHENTICATION_FAILED') ||
          error.message.includes('NO_VALID_SESSION')) {
        throw error;
      }
    }
    throw error;
  }
};
